import { useMemo, useState } from "react";
import { Bell, CalendarCheck2, CalendarClock, CalendarX2, CircleAlert, Loader2, MessageSquareWarning, Route, Wand2 } from "lucide-react";
import { useNavigate } from "react-router";
import { EmptyState } from "@/components/ui/empty-state";
import {
  activateWorkflow,
  createWorkflow,
  pauseWorkflow,
  useWorkflowList,
  type WorkflowSummary,
} from "@/hooks/use-workflows";
import { ApiError } from "@/lib/api-client";

type BookingAutomationRecipe = {
  id: string;
  eventType: string;
  label: string;
  description: string;
  defaultName: string;
  Icon: typeof CalendarClock;
};

const BOOKING_AUTOMATION_RECIPES: BookingAutomationRecipe[] = [
  {
    id: "booking-created",
    eventType: "booking.created",
    label: "New Booking Follow-up",
    description: "Kick off operational or nurture follow-up after a booking is created.",
    defaultName: "Booking Created Follow-up",
    Icon: CalendarClock,
  },
  {
    id: "booking-confirmed",
    eventType: "booking.confirmed",
    label: "Confirmed Booking Automation",
    description: "Send prep steps or next-best actions once a booking is confirmed.",
    defaultName: "Booking Confirmed Automation",
    Icon: CalendarCheck2,
  },
  {
    id: "booking-reminder",
    eventType: "booking.reminder",
    label: "Reminder Automation",
    description: "React to the canonical booking reminder event instead of a dead reminder-due alias.",
    defaultName: "Booking Reminder Automation",
    Icon: Bell,
  },
  {
    id: "booking-completed",
    eventType: "booking.completed",
    label: "Post-Visit Follow-up",
    description: "Request reviews, send care instructions, or trigger retention journeys after completion.",
    defaultName: "Booking Completed Follow-up",
    Icon: Route,
  },
  {
    id: "booking-cancelled",
    eventType: "booking.cancelled",
    label: "Cancellation Recovery",
    description: "Recover cancelled bookings with rebooking, retention, or escalation workflows.",
    defaultName: "Booking Cancelled Recovery",
    Icon: CalendarX2,
  },
  {
    id: "booking-no-show",
    eventType: "booking.no_show",
    label: "No-Show Recovery",
    description: "Launch no-show follow-up and rescheduling workflows from the live booking event stream.",
    defaultName: "Booking No-Show Recovery",
    Icon: MessageSquareWarning,
  },
  {
    id: "booking-walk-in-created",
    eventType: "booking.walk_in.created",
    label: "Walk-In Queue Follow-up",
    description: "React to new walk-ins with queue-aware notifications and downstream service handling.",
    defaultName: "Walk-In Queue Automation",
    Icon: CircleAlert,
  },
];

function getPrimaryTriggerEvent(workflow: WorkflowSummary): string | null {
  const trigger = workflow.triggers.find((item) => item.type === "trigger_event" && typeof item.eventType === "string");
  return trigger?.eventType ?? null;
}

function statusTone(status: string): string {
  if (status === "active") return "bg-[var(--color-success)]/10 text-[var(--color-success)]";
  if (status === "paused") return "bg-[var(--color-warning)]/10 text-[var(--color-warning)]";
  return "bg-[var(--color-muted)] text-[var(--color-muted-foreground)]";
}

export default function VisitAutomationsPage() {
  const navigate = useNavigate();
  const { workflows, isLoading, error, refetch } = useWorkflowList(undefined, "booking.*");
  const [pendingRecipeId, setPendingRecipeId] = useState<string | null>(null);
  const [pendingWorkflowId, setPendingWorkflowId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const workflowsByTrigger = useMemo(() => {
    const mapped = new Map<string, WorkflowSummary[]>();
    for (const workflow of workflows) {
      const eventType = getPrimaryTriggerEvent(workflow);
      if (!eventType) continue;
      const existing = mapped.get(eventType) ?? [];
      existing.push(workflow);
      mapped.set(eventType, existing);
    }
    return mapped;
  }, [workflows]);

  async function handleCreate(recipe: BookingAutomationRecipe) {
    setPendingRecipeId(recipe.id);
    setActionError(null);
    try {
      const workflow = await createWorkflow({
        name: recipe.defaultName,
        triggers: [{ type: "trigger_event", eventType: recipe.eventType }],
      });
      navigate(`/workflows/automations?workflowId=${encodeURIComponent(workflow.id)}`);
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Failed to create workflow.");
    } finally {
      setPendingRecipeId(null);
    }
  }

  async function handleToggle(workflow: WorkflowSummary) {
    setPendingWorkflowId(workflow.id);
    setActionError(null);
    try {
      if (workflow.status === "active") {
        await pauseWorkflow(workflow.id);
      } else {
        await activateWorkflow(workflow.id);
      }
      await refetch();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Failed to update workflow status.");
    } finally {
      setPendingWorkflowId(null);
    }
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div className="rounded-3xl border border-[var(--color-border)] bg-[var(--color-background)] p-6 shadow-[0_24px_70px_-50px_rgba(15,23,42,0.45)]">
        <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[var(--color-primary)]">Mounted booking automations</p>
        <h1 className="mt-3 text-3xl font-semibold tracking-tight text-[var(--color-foreground)]">Visit automations</h1>
        <p className="mt-3 max-w-3xl text-sm leading-6 text-[var(--color-muted-foreground)]">
          This surface is now wired to the canonical workflow engine. It only shows workflows triggered by the live booking
          event family and creates real workflow definitions instead of storing local automation state.
        </p>
      </div>

      {actionError && (
        <div className="rounded-2xl border border-[var(--color-error)]/20 bg-[var(--color-error)]/6 p-4 text-sm text-[var(--color-error)]">
          {actionError}
        </div>
      )}

      {error && (
        <div className="rounded-2xl border border-[var(--color-error)]/20 bg-[var(--color-error)]/6 p-4 text-sm text-[var(--color-error)]">
          {error}
        </div>
      )}

      {isLoading ? (
        <div className="rounded-3xl border border-[var(--color-border)] bg-[var(--color-background)] p-8 text-sm text-[var(--color-muted-foreground)]">
          Loading booking-trigger workflows...
        </div>
      ) : workflows.length === 0 ? (
        <EmptyState
          icon={<Wand2 className="h-8 w-8" />}
          title="No booking automations yet"
          description="Create the first booking-trigger workflow from one of the canonical trigger recipes below."
        />
      ) : null}

      <div className="grid gap-4 lg:grid-cols-2">
        {BOOKING_AUTOMATION_RECIPES.map((recipe) => {
          const relatedWorkflows = workflowsByTrigger.get(recipe.eventType) ?? [];
          const primaryWorkflow = relatedWorkflows[0] ?? null;
          const Icon = recipe.Icon;

          return (
            <section key={recipe.id} className="rounded-3xl border border-[var(--color-border)] bg-[var(--color-background)] p-5 shadow-[0_18px_50px_-40px_rgba(15,23,42,0.35)]">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="rounded-2xl bg-[var(--color-primary)]/10 p-3 text-[var(--color-primary)]">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-[var(--color-foreground)]">{recipe.label}</h2>
                    <p className="mt-2 text-sm leading-6 text-[var(--color-muted-foreground)]">{recipe.description}</p>
                    <p className="mt-3 font-mono text-xs text-[var(--color-muted-foreground)]">{recipe.eventType}</p>
                  </div>
                </div>
                <div className="rounded-full bg-[var(--color-surface)] px-3 py-1 text-xs font-medium text-[var(--color-muted-foreground)]">
                  {relatedWorkflows.length} workflow{relatedWorkflows.length === 1 ? "" : "s"}
                </div>
              </div>

              {primaryWorkflow ? (
                <div className="mt-5 rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-[var(--color-foreground)]">{primaryWorkflow.name}</span>
                    <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${statusTone(primaryWorkflow.status)}`}>
                      {primaryWorkflow.status}
                    </span>
                  </div>
                  <p className="mt-2 text-xs text-[var(--color-muted-foreground)]">
                    {relatedWorkflows.length > 1
                      ? `${relatedWorkflows.length} workflows currently listen to this trigger family.`
                      : "This trigger already has a live workflow bound to it."}
                  </p>

                  <div className="mt-4 flex flex-wrap gap-3">
                    <button
                      type="button"
                      onClick={() => navigate(`/workflows/automations?workflowId=${encodeURIComponent(primaryWorkflow.id)}`)}
                      className="inline-flex h-10 items-center justify-center rounded-2xl bg-[var(--color-primary)] px-4 text-sm font-medium text-white transition-opacity hover:opacity-90"
                    >
                      Open workflow
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleToggle(primaryWorkflow)}
                      disabled={pendingWorkflowId === primaryWorkflow.id}
                      className="inline-flex h-10 items-center justify-center rounded-2xl border border-[var(--color-border)] px-4 text-sm font-medium text-[var(--color-foreground)] transition-colors hover:bg-[var(--color-surface)] disabled:opacity-60"
                    >
                      {pendingWorkflowId === primaryWorkflow.id ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Updating
                        </>
                      ) : primaryWorkflow.status === "active" ? "Pause" : "Activate"}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="mt-5 rounded-2xl border border-dashed border-[var(--color-border)] bg-[var(--color-surface)] p-4">
                  <p className="text-sm text-[var(--color-muted-foreground)]">
                    No workflow is currently attached to this trigger.
                  </p>
                  <button
                    type="button"
                    onClick={() => void handleCreate(recipe)}
                    disabled={pendingRecipeId === recipe.id}
                    className="mt-4 inline-flex h-10 items-center justify-center rounded-2xl bg-[var(--color-primary)] px-4 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-60"
                  >
                    {pendingRecipeId === recipe.id ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Creating
                      </>
                    ) : "Create workflow"}
                  </button>
                </div>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}
